import { useState, useRef, useCallback, useEffect } from "react";
import { checkUser, signIn, faceSignIn } from "../utils/api";
import browserHistory from "../../history";

const getUploadUrl = async (sourcePath) => {
  const siteConf = (window.site && window.site.config) || {};
  const assetSourceBucket = siteConf.assetSourceBucket || "";
  const base = (window.site && window.site.submissionApiBaseUrl) || "";
  const appname = window.app && window.app.appName;
  const env = window.app && window.app.appEnvName;
  const token = (window.app && window.app.storage && window.app.storage.getItem("token")) || localStorage.getItem("token") || "";
  const resp = await fetch(
    `${base}/uploadURL?appname=${appname}&env=${env}&token=${token}`,
    {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ source_bucket: assetSourceBucket, sourcepath: sourcePath }),
    }
  );
  const data = await resp.json();
  const uploadUrl = data && data.result;
  if (!uploadUrl) throw new Error("Signed URL missing");
  return uploadUrl;
};

// ─── Webcam component ──────────────────────────────────────────────────────
function WebcamCapture({ onCapture, onError, onCancel }) {
  const videoRef = useRef(null);
  const canvasRef = useRef(null);
  const [streaming, setStreaming] = useState(false);
  const streamRef = useRef(null);

  const startCamera = async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({
        video: { facingMode: { ideal: "user" }, width: { ideal: 1280 }, height: { ideal: 720 } },
        audio: false,
      });
      streamRef.current = stream;
      setStreaming(true);
    } catch {
      onError("Camera access denied. Please allow camera access and try again.");
    }
  };

  useEffect(() => {
    if (streaming && videoRef.current && streamRef.current) {
      videoRef.current.srcObject = streamRef.current;
      videoRef.current.play().catch(() => {});
    }
  }, [streaming]);

  const stopCamera = () => {
    streamRef.current?.getTracks().forEach((t) => t.stop());
    setStreaming(false);
  };

  useEffect(() => () => stopCamera(), []);

  const capture = useCallback(() => {
    if (!videoRef.current || !canvasRef.current) return;
    const video = videoRef.current;
    const canvas = canvasRef.current;
    canvas.width = video.videoWidth;
    canvas.height = video.videoHeight;
    canvas.getContext("2d").drawImage(video, 0, 0);
    canvas.toBlob((blob) => {
      stopCamera();
      onCapture(blob, canvas.toDataURL("image/jpeg", 0.92));
    }, "image/jpeg", 0.92);
  }, [onCapture]);

  return (
    <div className="webcam-wrapper">
      {!streaming ? (
        <div className="action-row">
          <button className="btn-ghost" onClick={onCancel}>← Back</button>
          <button className="btn-primary" onClick={startCamera}><span className="material-symbols-outlined">photo_camera</span> Open Camera</button>
        </div>
      ) : (
        <>
          <div className="webcam-frame">
            <video ref={videoRef} playsInline muted autoPlay className="webcam-video" />
            <div className="face-oval" />
            <p className="webcam-hint">Center your face in the oval</p>
          </div>
          <div className="webcam-actions">
            <button className="btn-ghost" onClick={() => { stopCamera(); onCancel(); }}>Cancel</button>
            <button className="btn-primary" onClick={capture}>📸 Take Selfie →</button>
          </div>
        </>
      )}
      <canvas ref={canvasRef} style={{ display: "none" }} />
    </div>
  );
}

// ─── Modes ─────────────────────────────────────────────────────────────────
// landing         → Sign In / Sign Up choice
// signup_form     → email + password + role (all-in-one)
// signin_form     → email + password + face option (all-in-one)
// facecapture     → webcam
// faceprocess     → spinner
// ───────────────────────────────────────────────────────────────────────────

export default function StepAuth({ session, updateSession, onNext }) {
  const [mode, setMode] = useState("landing");
  const [email, setEmail] = useState(session.email || "");
  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [role, setRole] = useState(session.role || "creator");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const [facePreview, setFacePreview] = useState(null);

  // ── Per-field inline errors ─────────────────────────────────
  const [fieldErrors, setFieldErrors] = useState({ email: "", password: "", confirmPassword: "" });
  const [emailChecking, setEmailChecking] = useState(false);

  const setFieldError = (field, msg) => setFieldErrors(prev => ({ ...prev, [field]: msg }));

  const go = (nextMode) => {
    setError("");
    setFieldErrors({ email: "", password: "", confirmPassword: "" });
    setMode(nextMode);
  };

  // ── Email blur — validate format then check if already registered ──
  const handleEmailBlur = async () => {
    const val = email.trim();
    if (!val) return;
    const emailRx = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRx.test(val)) {
      setFieldError("email", "Enter a valid email address.");
      return;
    }
    if (mode !== "signup_form") return; // only check existence on signup
    setEmailChecking(true);
    setFieldError("email", "");
    try {
      const result = await checkUser(val);
      const { exists } = result?.result || {};
      if (exists) setFieldError("email", "An account with this email already exists. Please Sign In.");
    } catch { /* silent — full check still happens on submit */ }
    finally { setEmailChecking(false); }
  };

  // ── Confirm password blur — check match ─────────────────────
  const handleConfirmPasswordBlur = () => {
    if (!confirmPassword) return;
    if (password !== confirmPassword) {
      setFieldError("confirmPassword", "Passwords do not match.");
    } else {
      setFieldError("confirmPassword", "");
    }
  };

  // ── Password change — re-check match live if confirm was already touched ──
  const handlePasswordChange = (val) => {
    setPassword(val);
    setFieldError("password", val.length > 0 && val.length < 8 ? "Min. 8 characters." : "");
    if (confirmPassword) {
      setFieldError("confirmPassword", val !== confirmPassword ? "Passwords do not match." : "");
    }
  };

  // ── Sign Up: final guard then proceed ──────────────────────
  const handleSignUpContinue = async () => {
    if (!email.trim())             return setError("Enter your email address.");
    if (fieldErrors.email)         return;
    if (password.length < 8)       return setError("Password must be at least 8 characters.");
    if (password !== confirmPassword) return setError("Passwords do not match.");
    setLoading(true); setError("");
    try {
      const checkUserResult = await checkUser(email.trim());
      const { exists } = checkUserResult?.result || {};
      if (exists) { setFieldError("email", "An account with this email already exists. Please Sign In."); return; }
      updateSession({ email: email.trim(), password, role, userId: email.trim(), authMode: "signup" });
      onNext();
    } catch (e) { setError(e.message); }
    finally { setLoading(false); }
  };

  // ── Sign In: check email exists then sign in with password ──
  const handleSignInWithPassword = async () => {
    if (!email) return setError("Enter your email address.");
    if (!password) return setError("Enter your password.");
    setLoading(true); setError("");
    try {
      const checkUserResult = await checkUser(email);
      const { exists } = checkUserResult?.result || {};
      if (!exists) return setError("No account found with this email. Please Sign Up.");
      const response = await signIn(email, password);
      const result = (response && response.result) || response || {};
      redirectAfterLogin(result, {
        email:       result.emailid || email,
        userId:      result.userid  || result._id || email,
        role:        result.usertype || role,
        idToken:     result.token   || '',
        accessToken: result.token   || '',
      });
    } catch (e) { setError(e.message); }
    finally { setLoading(false); }
  };

  // ── Sign In: check email exists then go to face capture ─────
  const handleSignInWithFace = async () => {
    if (!email) return setError("Enter your email address.");
    setLoading(true); setError("");
    try {
      const checkUserResult = await checkUser(email);
      const { exists } = checkUserResult?.result || {};
      if (!exists) return setError("No account found with this email. Please Sign Up.");
      go("facecapture");
    } catch (e) { setError(e.message); }
    finally { setLoading(false); }
  };

  // ── Persist sign-in result into appStorage (mirrors Header.jsx) ─
  const applySession = (result) => {
    const appStorage = window.app && window.app.storage;
    if (appStorage) {
      appStorage.setItem('token',     result.token     || '');
      appStorage.setItem('userCode',  result.code      || '');
      appStorage.setItem('fullName',  (result.firstname || '') + ' ' + (result.lastname || ''));
      appStorage.setItem('userImage', result.userimage  || '');
      appStorage.setItem('firstname', result.firstname  || '');
      appStorage.setItem('lastname',  result.lastname   || '');
      appStorage.setItem('email',     result.emailid    || '');
      appStorage.setItem('user_id',   result.userid     || '');
      appStorage.setItem('status',    result.status     || '');
      appStorage.setItem('uid',       result._id        || '');
      appStorage.setItem('usertype',  result.usertype   || '');
    }
    localStorage.setItem('usertype', result.usertype || '');
  };

  const redirectAfterLogin = (result, sessionPatch) => {
    applySession(result);
    updateSession({ ...sessionPatch, authMode: "signin" });
    const appName = window.app && window.app.appName;
    const usertype = result.usertype || '';
    if (usertype === 'creator' || usertype === 'partner') {
      browserHistory.push('/creator?appname=' + appName);
      return;
    }
    onNext();
  };


  // ── Sign In: upload selfie → face-sign-in API ───────────────
  const handleFaceCapture = async (blob, dataUrl) => {
    setFacePreview(dataUrl);
    go("faceprocess");
    setLoading(true);
    try {
      const appname = window.app && window.app.appName;
      const sourcePath = `${appname}/facelogin/${email}/selfie${Date.now()}.jpg`;

      // 1. Upload selfie to S3 via signed URL PUT
      const signedUrl = await getUploadUrl(sourcePath);
      await new Promise((resolve, reject) => {
        const xhr = new XMLHttpRequest();
        xhr.addEventListener("load", () =>
          xhr.status >= 200 && xhr.status < 300 ? resolve() : reject(new Error("Selfie upload failed"))
        );
        xhr.addEventListener("error", () => reject(new Error("Selfie upload failed")));
        xhr.open("PUT", signedUrl);
        xhr.setRequestHeader("Content-Type", "image/jpeg");
        xhr.send(blob);
      });

      // 2. Call face-sign-in with S3 key
      const response = await faceSignIn(email, sourcePath);
      const result = (response && response.result) || response || {};
      redirectAfterLogin(result, {
        email:          result.emailid || email,
        userId:         result.userid  || result._id || email,
        role:           result.usertype || role,
        idToken:        result.token   || '',
        accessToken:    result.token   || '',
        faceLoginResult: result,
      });
    } catch (e) {
      setError(e.message);
      go("signin_options");
    } finally {
      setLoading(false);
    }
  };

  // ─────────────────────────────────────────────────────────────
  return (
    <div className="step-card">

      {/* ══ LANDING ══════════════════════════════════════════════ */}
      {mode === "landing" && (
        <>
          <div className="step-icon"><span className="material-symbols-outlined">lock</span></div>
          <h1>Identity Verification</h1>
          <p className="step-sub">Choose an option to get started.</p>
          <div style={{ display: "flex", flexDirection: "column", gap: "0.75rem", marginTop: "1.5rem" }}>
            <button
              className="btn-primary"
              style={{ padding: "0.85rem" }}
              onClick={() => { setEmail(""); setPassword(""); go("signin_form"); }}
            >
              Sign In
            </button>
            <button
              className="btn-ghost"
              style={{ padding: "0.85rem" }}
              onClick={() => { setEmail(""); setPassword(""); setConfirmPassword(""); go("signup_form"); }}
            >
              Sign Up
            </button>
          </div>
        </>
      )}

      {/* ══ SIGN UP — FULL FORM ═══════════════════════════════════ */}
      {mode === "signup_form" && (
        <>
          <div className="step-icon"><span className="material-symbols-outlined">star</span></div>
          <h1>Create Account</h1>
          <p className="step-sub">Fill in your details to get started.</p>
          {error && <div className="error-banner">{error}</div>}

          {/* Email — onBlur checks format + existing account */}
          <div className="field-group">
            <label>Email address</label>
            <div style={{ position: "relative" }}>
              <input
                type="email" value={email} autoFocus
                style={{ borderColor: fieldErrors.email ? "#ff5555" : undefined }}
                onChange={(e) => { setEmail(e.target.value); setError(""); setFieldError("email", ""); }}
                onBlur={handleEmailBlur}
                placeholder="you@example.com"
              />
              {emailChecking && (
                <span className="spinner" style={{ position: "absolute", right: "12px", top: "50%", transform: "translateY(-50%)" }} />
              )}
            </div>
            {fieldErrors.email && <span className="field-inline-error">{fieldErrors.email}</span>}
          </div>

          {/* Password — live length hint */}
          <div className="field-group">
            <label>Password</label>
            <input
              type="password" value={password}
              style={{ borderColor: fieldErrors.password ? "#ff5555" : undefined }}
              onChange={(e) => { handlePasswordChange(e.target.value); setError(""); }}
              placeholder="Create a strong password"
            />
            {fieldErrors.password
              ? <span className="field-inline-error">{fieldErrors.password}</span>
              : <span className="field-hint">Min. 8 characters with uppercase, number &amp; symbol</span>}
          </div>

          {/* Confirm password — onBlur checks match */}
          <div className="field-group">
            <label>Confirm password</label>
            <input
              type="password" value={confirmPassword}
              style={{ borderColor: fieldErrors.confirmPassword ? "#ff5555" : undefined }}
              onChange={(e) => {
                const val = e.target.value;
                setConfirmPassword(val);
                setError("");
                setFieldError("confirmPassword", val && val !== password ? "Passwords do not match." : "");
              }}
              onBlur={handleConfirmPasswordBlur}
              onKeyDown={(e) => e.key === "Enter" && handleSignUpContinue()}
              placeholder="Re-enter your password"
            />
            {fieldErrors.confirmPassword
              ? <span className="field-inline-error">{fieldErrors.confirmPassword}</span>
              : confirmPassword && confirmPassword === password
                ? <span className="field-inline-success">Passwords match ✓</span>
                : null}
          </div>

          <div className="field-group">
            <label>I am a</label>
            <div className="role-picker">
              <button type="button"
                className={`role-btn ${role === "creator" ? "active" : ""}`}
                onClick={() => setRole("creator")}>
                <span className="role-icon"><span className="material-symbols-outlined">groups_3</span></span>Creator
              </button>
              <button type="button"
                className={`role-btn ${role === "partner" ? "active" : ""}`}
                onClick={() => setRole("partner")}>
                <span className="role-icon"><span className="material-symbols-outlined">partner_exchange</span></span>Partner
              </button>
            </div>
          </div>
          <div style={{ display: "flex", flexDirection: "column", gap: "0.75rem", marginTop: "1rem" }}>
            <button className="btn-primary" onClick={handleSignUpContinue}
              disabled={loading || emailChecking || !!fieldErrors.email || !!fieldErrors.confirmPassword}>
              {loading ? "Checking…" : emailChecking ? "Verifying email…" : "Continue → Upload ID"}
            </button>
            <button className="btn-ghost" style={{ opacity: 0.6 }} onClick={() => go("landing")}>
              ← Back
            </button>
          </div>
        </>
      )}

      {/* ══ SIGN IN — FULL FORM ═══════════════════════════════════ */}
      {mode === "signin_form" && (
        <>
          <div className="step-icon"><span className="material-symbols-outlined">handshake</span></div>
          <h1>Welcome Back</h1>
          <p className="step-sub">Sign in to your account.</p>
          {error && <div className="error-banner">{error}</div>}
          <div className="field-group">
            <label>Email address</label>
            <input
              type="email" value={email} autoFocus
              onChange={(e) => { setEmail(e.target.value); setError(""); }}
              placeholder="you@example.com"
            />
          </div>
          <div className="field-group">
            <label>Password</label>
            <input
              type="password" value={password}
              onChange={(e) => { setPassword(e.target.value); setError(""); }}
              placeholder="Enter your password"
              onKeyDown={(e) => e.key === "Enter" && handleSignInWithPassword()}
            />
          </div>
          <div style={{ display: "flex", flexDirection: "column", gap: "0.75rem", marginTop: "1rem" }}>
            <button className="btn-primary" onClick={handleSignInWithPassword} disabled={loading}>
              {loading ? "Signing in…" : "Sign In →"}
            </button>
            <button
              className="btn-ghost"
              style={{ display: "flex", alignItems: "center", justifyContent: "center", gap: "0.6rem" }}
              onClick={handleSignInWithFace}
              disabled={loading}
            >
              <span className="material-symbols-outlined">familiar_face_and_zone</span> Sign In with Face
            </button>
            <button className="btn-ghost" style={{ opacity: 0.6 }} onClick={() => go("landing")}>
              ← Back
            </button>
          </div>
        </>
      )}

      {/* ══ FACE CAPTURE ═════════════════════════════════════════ */}
      {mode === "facecapture" && (
        <>
          <div className="step-icon"><span className="material-symbols-outlined">familiar_face_and_zone</span></div>
          <h1>Face Sign In</h1>
          <p className="step-sub">Look directly at the camera to authenticate.</p>
          {error && <div className="error-banner">{error}</div>}
          <WebcamCapture
            onCapture={handleFaceCapture}
            onError={(msg) => { setError(msg); go("signin_form"); }}
            onCancel={() => go("signin_form")}
          />
        </>
      )}

      {/* ══ FACE PROCESSING ══════════════════════════════════════ */}
      {mode === "faceprocess" && (
        <>
          <div className="step-icon"><span className="material-symbols-outlined">familiar_face_and_zone</span></div>
          <h1>Verifying…</h1>
          <p className="step-sub">Comparing your face to your registered profile.</p>
          <div className="processing-state">
            {facePreview && <img src={facePreview} alt="Your selfie" className="selfie-preview" />}
            <div className="processing-steps">
              <div className="proc-step active">
                <span className="spinner" /> Matching face to profile…
              </div>
            </div>
          </div>
        </>
      )}

    </div>
  );
}
